'use client';

import { AccountCard } from '@eliadmin/core/src/components/dlz/account/account-card';
import { PROFILE_COPY } from '@eliadmin/core/src/components/dlz/account/profile/profile-copy';
import { TextField } from '@eliadmin/core/src/components/dlz/account/profile/profile-fields';
import { REGISTER_COPY } from '@eliadmin/core/src/components/dlz/auth/register/register-copy';
import { Icon } from '@eliadmin/core/src/components/dlz/ui/icon';
import Link from '@eliadmin/core/src/components/link';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

/** Figma „Input“ State=Disabled: label, asterisk and helper in text/disabled too. */
const DISABLED_ROW = '[&>p]:text-[#9096A0] [&_label]:text-[#9096A0] [&_label_span]:text-[#9096A0]';

/**
 * Figma „Account type“ (60321:19144): bg/surface pill, 16 px icon in text/brand, DLZ/Body/S Medium.
 * The icons are drawn for 24 px with a 1.5 stroke; the design keeps 1.5 px at 16 px.
 */
function AccountType({ company }: { company: boolean }) {
  const { t } = useTranslations();

  return (
    <p className="flex shrink-0 items-center gap-1.5 rounded-full bg-[#F5F6F7] px-3 py-1.5">
      <Icon
        name={company ? 'briefcase' : 'user'}
        size={16}
        className="text-[#D7191F] [&_[stroke]]:[stroke-width:2.25]"
      />
      <span className="sr-only">{PROFILE_COPY.accountType}: </span>
      <span className="dlz-body-s-medium text-[#0F1012]">
        {company ? REGISTER_COPY.persona.company : t('text2234')}
      </span>
    </p>
  );
}

/**
 * Figma „Email change request“ (60321:19186; mobile 60459:88678): bg/surface box with the at icon.
 * Wide columns put the link at the right edge (radius 12), narrow ones stack it under the text with
 * the icon at the top (radius 18, as in the mobile frame).
 */
function EmailChangeNote() {
  return (
    <div className="flex items-start gap-3 rounded-[18px] bg-[#F5F6F7] p-4 @[40rem]:items-center @[40rem]:rounded-xl">
      <Icon name="at" size={20} className="text-[#D7191F]" />
      <div className="flex min-w-0 flex-1 flex-col items-start gap-2 @[40rem]:flex-row @[40rem]:items-center @[40rem]:justify-between @[40rem]:gap-3">
        <p className="dlz-body-s text-[#4F545C] @[40rem]:max-w-[466px]">
          {PROFILE_COPY.emailChange.text}
        </p>
        <Link
          href={ROUTES.CONTACT()}
          prefetch={false}
          className="dlz-body-s-medium shrink-0 rounded-sm text-[#D7191F] transition-colors hover:text-[#B5141A] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0F1012]"
        >
          {PROFILE_COPY.emailChange.link}
        </Link>
      </div>
    </div>
  );
}

/**
 * Figma „Osobné údaje“ (60321:19141; mobile 60459:88665): the account type, name and surname, the
 * login e-mail (read-only – it changes on request only) and the phone, two per row in wide columns.
 */
export function PersonalCard({ company }: { company: boolean }) {
  const { t } = useTranslations();

  return (
    <AccountCard title={t('text198')} aside={<AccountType company={company} />}>
      <div className="grid items-start gap-4 @[40rem]:grid-cols-2">
        <TextField
          name="name"
          label={t('text33')}
          required
          autoComplete="given-name"
          placeholder={t('text33')}
        />
        <TextField
          name="surname"
          label={t('text34')}
          required
          autoComplete="family-name"
          placeholder={t('text34')}
        />
        <TextField
          name="email"
          type="email"
          label={t('text35')}
          required
          disabled
          hint={PROFILE_COPY.emailHint}
          className={DISABLED_ROW}
        />
        <TextField
          name="phone"
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          label={t('text36')}
          required
          placeholder={REGISTER_COPY.personal.phonePlaceholder}
        />
      </div>
      <EmailChangeNote />
    </AccountCard>
  );
}
